import type { FC, ReactNode } from 'react'
import type { Filters, SearchOptions } from '@/types'
import type { SearchDraft } from '@/hooks/use-search-draft'

import { useEffect } from 'react'
import { Search } from 'lucide-react'
import { useSearchDraft } from '@/hooks/use-search-draft'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle
} from '@/components/ui/dialog'

import {
    AreaPicker,
    CountPills,
    PriceRange,
    TypePicker
} from '@/components/search/search-fields'

interface SearchDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    options: SearchOptions
    initial?: Partial<SearchDraft>
    keep?: Partial<Filters>
}

export const SearchDialog: FC<SearchDialogProps> = ({ open, onOpenChange, options, initial, keep }): ReactNode => {
    const { draft, update, reset, submit } = useSearchDraft(initial)

    useEffect(() => {
        if (open) reset(initial)
        // Start from the current search each time the window opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const handleSubmit = async () => {
        onOpenChange(false)
        await submit(keep)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='top-0 flex h-dvh max-h-dvh w-full max-w-none translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 sm:top-6 sm:h-auto sm:max-h-[calc(100dvh-3rem)] sm:max-w-2xl sm:rounded-3xl'>
                <div className='border-b border-border px-6 py-4 text-center'>
                    <DialogTitle className='text-base'>Find a home</DialogTitle>
                    <DialogDescription className='sr-only'>Choose an area, home type, price and bedrooms, then show matching homes.</DialogDescription>
                </div>

                <form
                    className='flex min-h-0 flex-1 flex-col'
                    onSubmit={event => {
                        event.preventDefault()
                        void handleSubmit()
                    }}
                >
                    <div className='flex-1 space-y-8 overflow-y-auto px-6 py-6'>
                        <section aria-labelledby='search-where'>
                            <h3 id='search-where' className='text-lg font-semibold'>Where</h3>

                            <label className='mt-3 flex items-center gap-3 rounded-xl border border-input px-4 py-3 focus-within:border-foreground focus-within:ring-1 focus-within:ring-foreground'>
                                <Search className='size-4 text-muted-foreground' aria-hidden='true' />
                                <span className='sr-only'>Search by city, neighborhood or home name</span>
                                <input
                                    value={draft.q}
                                    onChange={event => update({ q: event.target.value, area: '' })}
                                    placeholder='City, neighborhood or home name'
                                    className='w-full bg-transparent text-base outline-none placeholder:text-muted-foreground'
                                />
                            </label>

                            <div className='mt-4'>
                                <AreaPicker
                                    areas={options.areas}
                                    value={draft.area}
                                    onChange={area => update({ area, q: '' })}
                                    columns='grid-cols-3 sm:grid-cols-4'
                                />
                            </div>
                        </section>

                        <section aria-labelledby='search-type'>
                            <h3 id='search-type' className='mb-3 text-lg font-semibold'>Home type</h3>
                            <TypePicker types={options.types} value={draft.types} onChange={types => update({ types })} />
                        </section>

                        <section aria-labelledby='search-price'>
                            <h3 id='search-price' className='mb-3 text-lg font-semibold'>Price</h3>
                            <PriceRange
                                prices={options.prices}
                                minPrice={draft.minPrice}
                                maxPrice={draft.maxPrice}
                                onChange={(minPrice, maxPrice) => update({ minPrice, maxPrice })}
                            />
                        </section>

                        <section aria-labelledby='search-beds'>
                            <h3 id='search-beds' className='mb-3 text-lg font-semibold'>Bedrooms</h3>
                            <CountPills label='Bedrooms' value={draft.beds} onChange={beds => update({ beds })} />
                        </section>
                    </div>

                    <DialogFooter>
                        <button
                            type='button'
                            onClick={() => reset()}
                            className='rounded-lg px-2 py-1 text-sm font-semibold underline underline-offset-4 hover:bg-muted'
                        >
                            Clear all
                        </button>

                        <button
                            type='submit'
                            className='bg-brand-gradient flex h-12 items-center gap-2 rounded-xl px-6 text-base font-semibold text-white transition hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                        >
                            <Search className='size-4' aria-hidden='true' />
                            Show homes
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
